import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';

interface RoundFeedbackPromptProps {
  roundId: string;
  onSubmitFeedback: (message: string) => void;
  disabled?: boolean;
  title?: string;
  description?: string;
  placeholder?: string;
}

const RoundFeedbackPrompt: React.FC<RoundFeedbackPromptProps> = ({
  roundId,
  onSubmitFeedback,
  disabled = false,
  title,
  description,
  placeholder,
}) => {
  const { t } = useTranslation();
  const displayTitle = title ?? t('gameplay.feedback.title');
  const displayDescription = description ?? t('gameplay.feedback.description');
  const displayPlaceholder = placeholder ?? t('gameplay.feedback.placeholder');

  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setMessage('');
    setSubmitted(false);
  }, [roundId]);

  const handleSubmit = () => {
    const trimmed = message.trim();
    if (!trimmed || submitted || disabled) {
      return;
    }

    onSubmitFeedback(trimmed);
    setSubmitted(true);
  };

  return (
    <div
      style={{
        width: '100%',
        marginTop: '1rem',
        padding: '1.1rem',
        background: 'rgba(255, 255, 255, 0.88)',
        borderRadius: '12px',
        border: '1.5px solid rgba(var(--color-secondary-rgb), 0.28)',
        boxShadow: 'var(--shadow-sm)',
        boxSizing: 'border-box',
      }}
    >
      <h3
        style={{
          margin: 0,
          marginBottom: '0.45rem',
          fontSize: '1rem',
          lineHeight: 1.3,
          textAlign: 'center',
          color: 'var(--text-primary)',
        }}
      >
        {displayTitle}
      </h3>
      <p
        style={{
          margin: 0,
          marginBottom: '0.9rem',
          fontSize: '0.92rem',
          lineHeight: 1.45,
          textAlign: 'center',
          color: 'var(--text-secondary)',
        }}
      >
        {displayDescription}
      </p>

      {submitted ? (
        <p
          style={{
            margin: 0,
            textAlign: 'center',
            fontSize: '0.95rem',
            color: 'var(--text-primary)',
            fontWeight: 600,
          }}
        >
          {t('gameplay.feedback.thanksForFeedback')}
        </p>
      ) : (
        <>
          <textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={displayPlaceholder}
            disabled={disabled}
            rows={3}
            style={{
              width: '100%',
              padding: '0.9rem',
              borderRadius: '10px',
              border: '1px solid rgba(var(--color-secondary-rgb), 0.24)',
              background: 'rgba(255, 255, 255, 0.95)',
              color: 'var(--text-primary)',
              resize: 'vertical',
              boxSizing: 'border-box',
              font: 'inherit',
              marginBottom: '0.8rem',
            }}
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disabled || message.trim().length === 0}
            className="button"
            style={{
              width: '100%',
              opacity: disabled || message.trim().length === 0 ? 0.7 : 1,
              cursor:
                disabled || message.trim().length === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {t('common.submit')}
          </button>
        </>
      )}
    </div>
  );
};

export default RoundFeedbackPrompt;
